import { useCallback, useEffect, useRef } from 'react'

type ErrorObject = {
  status?: number
  statusCode?: number
  statusText?: string
  message?: string
  isNotFound?: boolean
  isForbidden?: boolean
  isBadRequest?: boolean
  isServerError?: boolean
  isClientError?: boolean
}

type ErrorHandlers = {
  onNotFound?: () => void
  onForbidden?: () => void
  onBadRequest?: () => void
  onUnauthorized?: () => void
  onServerError?: () => void
  onClientError?: () => void
  onUnknownError?: (error: ErrorObject) => void
}

export function useErrorHandler(
  error: ErrorObject | null,
  handlers: ErrorHandlers = {}
) {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  const handleError = useCallback((error: ErrorObject) => {
    const currentHandlers = handlersRef.current
    const statusCode = error.statusCode || error.status
    let handled = false

    switch (statusCode) {
      case 404:
        if (currentHandlers.onNotFound) {
          currentHandlers.onNotFound()
          handled = true
        }
        break
      case 403:
        if (currentHandlers.onForbidden) {
          currentHandlers.onForbidden()
          handled = true
        }
        break
      case 400:
        if (currentHandlers.onBadRequest) {
          currentHandlers.onBadRequest()
          handled = true
        }
        break
      case 401:
        if (currentHandlers.onUnauthorized) {
          currentHandlers.onUnauthorized()
          handled = true
        }
        break
      case 500:
        if (currentHandlers.onServerError) {
          currentHandlers.onServerError()
          handled = true
        }
        break
    }

    if (!handled) {
      if (statusCode && statusCode >= 500) {
        if (currentHandlers.onServerError) {
          currentHandlers.onServerError()
        } else {
          currentHandlers.onUnknownError?.(error)
        }
      } else if (statusCode && statusCode >= 400) {
        if (currentHandlers.onClientError) {
          currentHandlers.onClientError()
        } else {
          currentHandlers.onUnknownError?.(error)
        }
      } else {
        currentHandlers.onUnknownError?.(error)
      }
    }
  }, [])

  useEffect(() => {
    if (!error) return
    handleError(error)
  }, [error, handleError])

  const statusCode = error?.statusCode || error?.status

  return {
    statusCode,
    isNotFound: error?.isNotFound || statusCode === 404,
    isForbidden: error?.isForbidden || statusCode === 403,
    isBadRequest: error?.isBadRequest || statusCode === 400,
    isUnauthorized: statusCode === 401,
    isServerError:
      error?.isServerError || (statusCode !== undefined && statusCode >= 500),
    isClientError:
      error?.isClientError ||
      (statusCode !== undefined && statusCode >= 400 && statusCode < 500),
    hasError: !!error,
  }
}
